/* eslint-disable complexity, max-lines */
import {
  first_nested_value,
  first_value,
  normalize_text,
  title_case
} from '@/services/onboarding_data_helpers';

const LEGACY_JOURNEY_LABELS = new Map([
  ['supplier', 'Supplier'],
  ['employee', 'Employee'],
  ['employees', 'Employee'],
  ['merchant', 'Merchant'],
  ['partner', 'Partner'],
  ['intern', 'Intern'],
  ['client', 'Client']
]);

const JOURNEY_ALIASES = new Map([
  ['supplier', ['supplier']],
  ['employee', ['employee', 'employees', 'staff']],
  ['merchant', ['merchant']],
  ['partner', ['partner']],
  ['intern', ['intern', 'internship']],
  ['client', ['client', 'customer']]
]);

const ONBOARDING_JOURNEY_KEYS = ['supplier', 'employee', 'merchant', 'partner', 'intern', 'client'];
const LOCAL_ONBOARDING_CASE_METADATA_KEY = 'onboarding_case_metadata';

const READY_WORDS = ['ready', 'approved', 'active', 'activated', 'complete', 'completed'];
const BLOCKED_WORDS = ['blocked', 'declined', 'rejected', 'failed', 'expired'];
const PENDING_WORDS = ['pending', 'awaiting', 'review'];

const get_entity_type_name = (record) => first_value([
  first_nested_value(record, [
    'entity_type.entity_type',
    'entity_type.entity_type_name',
    'entity_type.name',
    'current_entity_type.entity_type',
    'current_entity_type.entity_type_name',
    'entity_type_entity_profile.entity_type.entity_type',
    'entity_type_entity_profile.entity_type.entity_type_name',
    'entity.entity_type.entity_type',
    'entity.entity_type.entity_type_name'
  ]),
  typeof record?.entity_type === 'string' ? record.entity_type : '',
  record?.entity_type_name,
  typeof record?.current_entity_type === 'string' ? record.current_entity_type : '',
  record?.current_entity_type_name,
  record?.journey
]);

const get_entity_type_id = (record) => first_value([
  record?.threshold_entity_type_id_used,
  first_nested_value(record, [
    'entity_type_entity_profile.entity_type_id',
    'entity_type_entity_profile.entity_type.id',
    'entity_type.id',
    'current_entity_type.id',
    'entity.entity_type_id',
    'entity.entity_type.id'
  ]),
  record?.entity_type_id
], null);

function get_project_id(record) {
  return first_value([
  record?.project_id,
  record?.onboarding_project_id,
  first_nested_value(record, [
    'project.id',
    'project.project_id',
    'entity.project_id',
    'current_entity.project_id',
    'entity.project.id',
    'current_entity.project.id'
  ])
  ], null);
}

const get_record_status_name = (record) => first_value([
  record?.current_workflow_status,
  record?.application_status,
  record?.assignment_status_name,
  first_nested_value(record, ['status.status', 'status.name']),
  record?.status_name,
  record?.status
]);

const record_matches_journey_context = (record, journey_key) => {
  const aliases = JOURNEY_ALIASES.get(journey_key) || [journey_key];
  const haystack = [
    get_entity_type_name(record),
    record?.journey,
    record?.journey_tone,
    record?.current_entity_type
  ]
    .map(normalize_text)
    .join(' ');

  return aliases.some((alias) => haystack.includes(alias));
};

export const record_matches_journey = (record, journey_key) => (
  record_matches_journey_context(record, journey_key)
);

export const infer_journey_key = (record) => {
  const entity_type_id = get_entity_type_id(record);
  if (entity_type_id) return String(entity_type_id);

  const context_journey = ONBOARDING_JOURNEY_KEYS.find((journey_key) => record_matches_journey_context(record, journey_key));
  return context_journey || normalize_text(get_entity_type_name(record)) || 'onboarding';
};

export const infer_journey_key_from_name = (name) => {
  const normalized_name = normalize_text(name);
  if (!normalized_name) return '';

  for (const [journey_key, aliases] of JOURNEY_ALIASES.entries()) {
    if (aliases.some((alias) => normalized_name.includes(alias))) {
      return journey_key;
    }
  }

  return normalized_name.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || '';
};

function resolve_journey_key(record, provided_journey_key = '') {
  const entity_type_id = get_entity_type_id(record);
  if (entity_type_id) return String(entity_type_id);
  return provided_journey_key || infer_journey_key(record);
}

function resolve_journey_label(record, journey_key = '') {
  return first_value([
    get_entity_type_name(record),
    record?.journey,
    LEGACY_JOURNEY_LABELS.get(journey_key),
    title_case(journey_key)
  ], 'Onboarding');
}

export const get_form_id = (form) => first_value([
  form?.form?.id,
  form?.form?.form_id,
  form?.id,
  form?.form_id
], null);

export const get_form_name = (form) => first_value([
  form?.form?.name,
  form?.form?.form_name,
  form?.form?.title,
  form?.name,
  form?.form_name,
  form?.title
], 'Form');

const get_owner_name = (record) => {
  const owner = first_value([
    record?.assigned_to_name,
    first_nested_value(record, ['assigned_administrator.full_name', 'assigned_administrator.name']),
    record?.assigned_administrator_name
  ]);

  if (owner) return owner;

  const first_name = first_nested_value(record, ['assigned_administrator.first_name']);
  const last_name = first_nested_value(record, ['assigned_administrator.last_name']);
  return `${first_name} ${last_name}`.trim() || 'Unassigned';
};

const get_name_words = (name) => {
  const normalized_name = String(name || '').trim();
  return normalized_name ? normalized_name.split(/\s+/).filter(Boolean) : [];
};

const get_initials = (name) => {
  const words = get_name_words(name);
  if (!words.length) return '--';

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

const get_status_name = (record) => get_record_status_name(record);

const get_activation_tone = (status_name, workflow_complete) => {
  const status = normalize_text(status_name);

  if (workflow_complete === true || READY_WORDS.some((word) => status.includes(word))) return 'ready';
  if (BLOCKED_WORDS.some((word) => status.includes(word))) return 'blocked';
  if (PENDING_WORDS.some((word) => status.includes(word))) return 'pending';
  return 'progress';
};

function normalize_progress(value) {
  const progress = Number(value);
  if (!Number.isFinite(progress)) return null;
  return Math.max(0, Math.min(100, Math.round(progress)));
}

function get_completion_score(completion = {}) {
  if (completion?.overall_complete === true) return 100;

  const completion_values = [
    completion?.forms_complete,
    completion?.workflow_complete,
    completion?.documents_complete,
    completion?.invoicing_complete,
    completion?.children_complete
  ].filter((value) => value !== undefined && value !== null);

  if (!completion_values.length) return null;

  // Treat a brand-new application as not started until the core onboarding
  // flow itself has begun, even if optional/non-applicable buckets report true.
  if (completion?.forms_complete !== true && completion?.workflow_complete !== true) {
    return 0;
  }

  const complete_count = completion_values.filter((value) => value === true).length;
  return Math.round((complete_count / completion_values.length) * 100);
}

function get_runtime_progress(record) {
  const profile_completion_percentage = normalize_progress(first_value([
    record?.profile_completion?.completion_percentage,
    record?.profile_completion_percentage
  ], NaN));
  if (profile_completion_percentage !== null) return profile_completion_percentage;

  const status_payload = first_value([
    record?.onboarding_status?.data,
    record?.onboarding_status
  ], {});
  const completion = status_payload?.completion || record?.onboarding_completion || {};
  const completion_score = get_completion_score(completion);
  if (completion_score !== null) return completion_score;
  if (status_payload?.parent_complete === true || record?.parent_complete === true) return 100;
  return null;
}

const get_progress = (record, activation_tone) => {
  const runtime_progress = get_runtime_progress(record);
  if (runtime_progress !== null) return runtime_progress;

  const explicit_progress = normalize_progress(first_value([
    record?.progress,
    record?.completion_percentage,
    record?.percentage_complete,
    record?.form_progress
  ], NaN));

  if (explicit_progress !== null) return explicit_progress;
  if (record?.workflow_complete === true || activation_tone === 'ready') return 100;
  if (activation_tone === 'blocked') return 0;
  return 0;
};

const get_progress_tone = (progress, activation_tone) => {
  if (activation_tone === 'blocked' || progress < 70) return 'warning';
  if (activation_tone === 'ready' || progress >= 90) return 'success';
  return 'navy';
};

const get_risk = (record) => {
  const risk_assessment_level = (
    Array.isArray(record?.onboarding_status?.risk_assessments)
      ? record.onboarding_status.risk_assessments
        .map((assessment) => first_value([
          assessment?.risk_level,
          assessment?.result?.risk_level,
          assessment?.latest_result?.risk_level
        ], ''))
        .find(Boolean)
      : ''
  );
  const risk = first_value([
    record?.risk,
    record?.risk_rating,
    record?.risk_level,
    record?.risk_result?.risk_level,
    record?.risk_category,
    risk_assessment_level,
    record?.compliance_bucket,
    first_nested_value(record, [
      'threshold_assessment.risk',
      'threshold_assessment.risk_level'
    ])
  ], '');

  const normalized_risk = normalize_text(risk);
  if (['high', 'medium', 'moderate', 'low'].some((level) => normalized_risk.includes(level))) {
    return title_case(risk);
  }

  return 'Not assessed';
};

const get_risk_tone = (risk) => {
  const normalized = normalize_text(risk);
  if (normalized.includes('high')) return 'high';
  if (normalized.includes('medium') || normalized.includes('moderate')) return 'medium';
  if (normalized.includes('low')) return 'low';
  return 'pending';
};

function get_application_id(record) {
  return first_value([
    record?.application_id,
    record?.id
  ], null);
}

function get_created_at(record) {
  return first_value([
    record?.created_at,
    record?.date_created,
    record?.created_on,
    record?.created,
    record?.application_created_at,
    record?.onboarding_created_at,
    first_nested_value(record, [
      'application.created_at',
      'application.date_created',
      'entity_profile.created_at',
      'profile.created_at',
      'current_entity.created_at',
      'entity.created_at'
    ])
  ], '');
}

function get_created_order_value(record) {
  const parsed_created_at = Date.parse(get_created_at(record));
  if (!Number.isNaN(parsed_created_at)) return parsed_created_at;

  const numeric_application_id = Number(get_application_id(record) || 0);
  return numeric_application_id > 0 ? numeric_application_id : 0;
}

const get_entity_profile_id = (record) => first_value([
  record?.entity_profile_id,
  record?.entity,
  record?.current_entity,
  record?.id
], null);

const get_display_name = (record) => first_value([
  record?.display_name,
  record?.name,
  record?.entity_name,
  record?.current_entity_name,
  first_nested_value(record, ['entity.name', 'current_entity.name']),
  record?.trust_number
], 'Unnamed onboarding');

const read_local_onboarding_case_metadata = () => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = sessionStorage.getItem(LOCAL_ONBOARDING_CASE_METADATA_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const write_local_onboarding_case_metadata = (items) => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(LOCAL_ONBOARDING_CASE_METADATA_KEY, JSON.stringify(items));
};

const find_local_case_metadata = (record) => {
  const application_id = get_application_id(record);
  const entity_profile_id = get_entity_profile_id(record);
  const entity_type_id = get_entity_type_id(record);
  const journey_key = infer_journey_key(record);
  const display_name = get_display_name(record);

  return read_local_onboarding_case_metadata().find((item) => (
    (application_id && Number(item?.application_id) === Number(application_id))
    || (
      entity_profile_id
      && entity_type_id
      && Number(item?.entity_profile_id) === Number(entity_profile_id)
      && Number(item?.entity_type_id) === Number(entity_type_id)
    )
    || (
      item?.journey_key === journey_key
      && normalize_text(item?.entity_name) === normalize_text(display_name)
    )
  )) || null;
};

const get_local_metadata_value = (metadata_value, record_value) => metadata_value || record_value;

const merge_local_case_metadata = (record, metadata) => ({
  ...record,
  onboarding_email: get_local_metadata_value(metadata.email, record?.onboarding_email),
  onboarding_activate_by: get_local_metadata_value(metadata.activate_by, record?.onboarding_activate_by),
  onboarding_owner_role_name: get_local_metadata_value(metadata.owner_role_name, record?.onboarding_owner_role_name),
  onboarding_owner_role_names: get_local_metadata_value(metadata.owner_role_names, record?.onboarding_owner_role_names),
  onboarding_project_name: get_local_metadata_value(metadata.project_name, record?.onboarding_project_name),
  onboarding_template_names: get_local_metadata_value(metadata.template_names, record?.onboarding_template_names),
  onboarding_created_at: get_local_metadata_value(metadata.updated_at, record?.onboarding_created_at),
  entity_name: get_local_metadata_value(metadata.entity_name, record?.entity_name),
  current_entity_name: get_local_metadata_value(metadata.entity_name, record?.current_entity_name)
});

const apply_local_case_metadata = (record) => {
  const metadata = find_local_case_metadata(record);
  return metadata ? merge_local_case_metadata(record, metadata) : record;
};

const get_selected_role_name = (selected_roles = []) => first_value([
  selected_roles[0]?.name,
  selected_roles[0]?.role,
  selected_roles[0]?.role_name
], '');

const get_selected_role_names = (selected_roles = []) => selected_roles
  .map((role) => first_value([role?.name, role?.role, role?.role_name], ''))
  .filter(Boolean);

const get_selected_template_names = (selected_templates = []) => selected_templates
  .map((form) => first_value([form?.name, form?.form_name, form?.title], ''))
  .filter(Boolean);

export const build_local_onboarding_case_metadata = ({
  application_id,
  entity_profile_id,
  onboarding_form,
  selected_project,
  selected_entity_type,
  selected_roles,
  selected_templates
}) => ({
  application_id,
  entity_profile_id,
  entity_name: String(onboarding_form?.entity_name || '').trim(),
  email: String(onboarding_form?.email || '').trim(),
  activate_by: String(onboarding_form?.activate_by || '').trim(),
  owner_role_name: get_selected_role_name(selected_roles),
  owner_role_names: get_selected_role_names(selected_roles),
  project_name: first_value([selected_project?.name, selected_project?.project_name, selected_project?.title], ''),
  template_names: get_selected_template_names(selected_templates),
  journey_key: String(selected_entity_type?.id || selected_entity_type?.journey_key || infer_journey_key_from_name(selected_entity_type?.name || '')),
  entity_type_id: selected_entity_type?.id || null,
  updated_at: new Date().toISOString()
});

export const map_case = (record, journey_key) => {
  const normalized_record = apply_local_case_metadata(record);
  const resolved_journey_key = resolve_journey_key(normalized_record, journey_key);
  const journey = resolve_journey_label(normalized_record, resolved_journey_key);
  const status_name = get_status_name(normalized_record);
  const display_status = title_case(status_name);
  const activation_tone = get_activation_tone(status_name, normalized_record?.workflow_complete);
  const progress = get_progress(normalized_record, activation_tone);
  const risk = get_risk(normalized_record);
  const application_id = get_application_id(normalized_record);
  const entity_profile_id = get_entity_profile_id(normalized_record);
  const display_name = get_display_name(normalized_record);
  const owner = first_value([
    normalized_record?.onboarding_owner_role_name,
    get_owner_name(normalized_record)
  ], 'Unassigned');

  return ({
    ...normalized_record,
    id: application_id || `${resolved_journey_key}-${entity_profile_id || display_name}`,
    application_id,
    entity_profile_id,
    entity_type_id: get_entity_type_id(normalized_record),
    project_id: get_project_id(normalized_record),
    created_at: get_created_at(normalized_record),
    created_order_value: get_created_order_value(normalized_record),
    name: display_name,
    display_name,
    detail: first_value([normalized_record?.detail, status_name], ''),
    journey,
    journey_key: resolved_journey_key,
    journey_tone: resolved_journey_key,
    progress,
    progress_tone: get_progress_tone(progress, activation_tone),
    risk,
    risk_level: risk,
    risk_score: first_value([normalized_record?.risk_score, normalized_record?.risk_result?.total_score], normalized_record?.risk_score),
    risk_tone: get_risk_tone(risk),
    stage: status_name,
    owner,
    owner_initials: get_initials(owner),
    eta: first_value([normalized_record?.eta, normalized_record?.due_in, normalized_record?.sla, normalized_record?.onboarding_activate_by], '-'),
    activation: display_status,
    activation_tone
  });
};

function compare_cases_newest_first(left, right) {
  const right_value = Number(right?.case_item?.created_order_value || 0);
  const left_value = Number(left?.case_item?.created_order_value || 0);
  if (right_value !== left_value) return right_value - left_value;

  return left.index - right.index;
}

export const sort_cases_newest_first = (cases = []) => {
  const sorted_cases = cases
    .map((case_item, index) => ({ case_item, index }))
    .sort(compare_cases_newest_first)
    .map((entry) => entry.case_item);

  return sorted_cases;
};

export const calculate_stats = (cases, journey_label) => {
  const blocked_count = cases.filter((item) => item.activation_tone === 'blocked').length;
  const ready_count = cases.filter((item) => item.activation_tone === 'ready').length;

  return [
    { label: 'Active', value: String(cases.length), detail: `${journey_label} onboardings`, tone: 'text-black' },
    { label: 'Blocked', value: String(blocked_count), detail: 'Resolve to proceed', tone: 'text-danger' },
    { label: 'Ready', value: String(ready_count), detail: 'Activate today', tone: 'text-success' },
    { label: 'Avg time', value: '-', detail: 'To activation', tone: 'text-black' }
  ];
};
